import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';

// All portal endpoints (`/me/...`, `/pain-level-survey`, the survey +
// subscription routes, etc.) live on healthcom-backend-schedule, so they
// share the schedule API name. The legacy healthcom-backend gateway is no
// longer referenced anywhere in this app.
const HEALTHCOM_API_NAME = 'api';
const HEALTHCOM_API_NOAUTH_NAME = 'api-noauth';

const healthcomService = () => {
  /**
   * Fetch the authenticated patient's data from the HealthCom API.
   * This returns patient info, conditions, exercises, advice, and clinic details.
   * When a phone/email maps to multiple clinics, the response includes a `clinics` array.
   * When a clinic has multiple patients sharing the credential, `patients` is populated.
   */
  const me = async (): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, '/me', {});
  };

  /**
   * Discover which clinics the authenticated user belongs to.
   * Returns { clinics: PatientClinic[] } with path (vanity slug) for each.
   */
  const meClinics = async (): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, '/me/clinics', {});
  };

  /**
   * Fetch portal data (exercises, conditions, advice) for the authenticated user
   * at a specific clinic identified by its vanity path.
   * Returns { patients: PatientInfo[] }.
   */
  const mePortal = async (path: string): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, `/me/${path}/portal`, {});
  };

  /**
   * Fetch portal data by clinicId instead of path. Used after the user has
   * selected a clinic in the authed portal — clinics without a vanity URL
   * path are still reachable this way.
   * Returns { patients: PatientInfo[] }.
   */
  const mePortalByClinicId = async (clinicId: number): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, `/me/clinic/${clinicId}/portal`, {});
  };

  /**
   * Initiate portal login — ensures the Cognito user exists for the given email/phone.
   * Called before signIn() to create the user if they don't exist yet.
   */
  const loginPortal = async ({
    email,
    phone,
  }: {
    email?: string;
    phone?: string;
  }): Promise<any> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NOAUTH_NAME, '/login/portal', {
      body: { email, phone },
    });
  };

  /**
   * Submit a pain level survey response after completing exercises.
   * `patientId` is required — one Cognito login can have several
   * patient records (parent + kids), so the backend won't try to
   * resolve a single owner.
   */
  const painLevelSurvey = async ({
    level,
    patientId,
  }: {
    level: number;
    patientId: string;
  }): Promise<any> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NAME, '/pain-level-survey', {
      body: { level, patientId },
    });
  };

  /**
   * Pay down the patient's outstanding balance using a saved
   * USAePay card on file. The backend allocates the amount across
   * the patient's open invoices oldest-first; the patient doesn't
   * pick which transactions to apply against. Pass `payMethodKey`
   * to charge a specific saved card; omit it to default to the
   * first one on the customer record.
   */
  const payBalance = async ({
    patientId,
    amount,
    payMethodKey,
    locationId,
  }: {
    patientId: string;
    amount: number;
    payMethodKey?: string;
    // Which location's balance is being paid. Required by the backend only
    // when the clinic has split-balance-by-location active.
    locationId?: number;
  }): Promise<{
    message?: string;
    amount?: number;
    invoicesPaid?: number;
    data?: { billingKey?: string; refnum?: string };
  }> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NAME, `/me/${patientId}/pay-balance`, {
      body: { amount, payMethodKey, locationId },
    });
  };

  /**
   * List the patient's saved payment methods (USAePay + Dejavoo)
   * so the Balance page can render a card picker. Each card is
   * tagged with its processor and a `chargeable` flag — only
   * `chargeable: true` cards can be charged from the portal today.
   * Also returns `addCard` config (processor + ecommToken) when the
   * clinic supports adding cards from the portal.
   */
  const listPaymentMethods = async (
    patientId: string
  ): Promise<{
    data: {
      key: string;
      processor: 'usaepay' | 'dejavoo';
      cardholder?: string;
      cardType?: string;
      lastFour?: string;
      isDefault?: boolean;
      // When true, the processing fee is waived for transactions paid with
      // this saved card (no toggle is presented to the patient).
      suppressProcessingFee?: boolean;
      chargeable: boolean;
      // Merchant location for split-balance clinics. The card picker uses this
      // to show only cards chargeable at the patient's selected location.
      locationId?: number;
    }[];
    addCard?:
      | { processor: 'dejavoo'; ecommToken: string }
      | { processor: 'usaepay'; epayPublicKey: string }
      | null;
    // Per-location add-card configs (split-balance-by-location only), keyed by
    // locationId, so the portal tokenizes a card against the merchant of the
    // location it's being added for.
    addCardByLocation?: Record<
      number,
      | { processor: 'dejavoo'; ecommToken: string }
      | { processor: 'usaepay'; epayPublicKey: string }
      | null
    >;
    // Clinic-level processing fee rate as a decimal (e.g. 0.029 for 2.9%).
    // 0 when the clinic doesn't charge a processing fee.
    processingFeeRate?: number;
    splitBalanceByLocation?: boolean;
  }> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, `/me/${patientId}/payment-methods`, {});
  };

  /**
   * Save a new payment method for the patient. The backend branches by
   * processor on the token shape it receives:
   *   - Dejavoo: `paymentTokenId` from the iPOSpays Freedom-to-Design SDK.
   *   - USAePay: `paymentKey` from the hosted PayJS client.
   * The frontend tokenizes the card client-side and sends only the token
   * (+ optional metadata); the PAN never crosses our wire.
   */
  const addPaymentMethod = async ({
    patientId,
    paymentTokenId,
    paymentKey,
    cardholderName,
    postalCode,
    cardLastFour,
    cardType,
    locationId,
  }: {
    patientId: string;
    paymentTokenId?: string;
    paymentKey?: string;
    cardholderName?: string;
    postalCode?: string;
    cardLastFour?: string;
    cardType?: string;
    // Under split-balance-by-location, the location whose merchant the card
    // should be saved against (matches the merchant it was tokenized on).
    locationId?: number;
  }): Promise<{
    message?: string;
    data?: {
      key: string;
      processor: 'dejavoo' | 'usaepay';
      cardLastFour?: string | null;
      cardType?: string | null;
      cardholder?: string | null;
    };
  }> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NAME, `/me/${patientId}/payment-methods`, {
      body: {
        paymentTokenId,
        paymentKey,
        cardholderName,
        postalCode,
        cardLastFour,
        cardType,
        locationId,
      },
    });
  };

  /**
   * Toggle `Patient.unsubscribe` (gates 30-day post-care survey emails)
   * for the named patient. The body's `patientId` must name a patient
   * the active session owns — backend verifies via `getOwnedPatientIds`,
   * matching the painLevelSurvey ownership check.
   */
  const surveyEmails = async (unsubscribe: 0 | 1, patientId: string): Promise<any> => {
    return ChiroUpAPI.put(HEALTHCOM_API_NAME, '/survey-emails', {
      body: { unsubscribe, patientId },
    });
  };

  return {
    me,
    meClinics,
    mePortal,
    mePortalByClinicId,
    loginPortal,
    painLevelSurvey,
    payBalance,
    listPaymentMethods,
    addPaymentMethod,
    surveyEmails,
  };
};

export default healthcomService();

export { HEALTHCOM_API_NAME, HEALTHCOM_API_NOAUTH_NAME };
